/**
 * CreateBatchDialog Component
 *
 * Dialog for creating a new batch operation with operation type, partner account,
 * and location filter selection. Shows three primary operations as cards:
 * Enrich, Analyze Tags, and Publish.
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, Loader2, Sparkles, Tags, Globe, Pencil, Bot, Shapes, RefreshCw } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { usePartnerAccounts } from '@/api/partner-accounts';
import { useGetBatchPreview, useCreateBatchOperation } from '@/api/batch-operations';
import type { BatchOperationPreview, BatchLocationFilter } from '@/api/batch-operations';
import { useToast } from '@/components/ui/use-toast';
import { BulkUpdateAttributesForm } from './BulkUpdateAttributesForm';

interface CreateBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPartnerAccountId?: string;
  onSuccess?: () => void;
}

// Four primary operation types shown as cards
const OPERATION_TYPES = [
  {
    value: 'auto_enrich' as const,
    label: 'Enrich',
    description: 'Auto-populate enrichment fields with sensible defaults',
    icon: Sparkles,
    filters: [
      { value: 'non_enriched' as BatchLocationFilter, label: 'Non-enriched', description: 'Not yet enriched' },
      { value: 'enriched' as BatchLocationFilter, label: 'Enriched', description: 'Already enriched (re-enrich)' },
    ],
    defaultFilter: 'non_enriched' as BatchLocationFilter,
  },
  {
    value: 'batch_tags' as const,
    label: 'Analyze Tags',
    description: 'Run AI tag analysis on location content and images',
    icon: Tags,
    filters: [
      { value: 'non_tagged' as BatchLocationFilter, label: 'Non-tagged', description: 'No tags analyzed yet' },
      { value: 'tagged' as BatchLocationFilter, label: 'Tagged', description: 'Already tagged (re-analyze)' },
    ],
    defaultFilter: 'non_tagged' as BatchLocationFilter,
  },
  {
    value: 'batch_publish' as const,
    label: 'Publish',
    description: 'Publish enriched locations to ScoutAround marketplace',
    icon: Globe,
    filters: [
      { value: 'non_published' as BatchLocationFilter, label: 'Non-published', description: 'Not yet published' },
      { value: 'published' as BatchLocationFilter, label: 'Published', description: 'Already published (re-sync)' },
    ],
    defaultFilter: 'non_published' as BatchLocationFilter,
  },
  {
    value: 'bulk_update' as const,
    label: 'Bulk Update',
    description: 'Set field values in bulk on selected locations',
    icon: Pencil,
    filters: [
      { value: 'all' as BatchLocationFilter, label: 'All', description: 'All locations' },
      { value: 'non_enriched' as BatchLocationFilter, label: 'Non-enriched', description: 'Not yet enriched' },
      { value: 'enriched' as BatchLocationFilter, label: 'Enriched', description: 'Already enriched' },
      { value: 'non_published' as BatchLocationFilter, label: 'Non-published', description: 'Not yet published' },
    ],
    defaultFilter: 'all' as BatchLocationFilter,
  },
  {
    value: 'ai_content' as const,
    label: 'AI Content',
    description: 'Generate AI-powered title and description for any partner listing.',
    icon: Bot,
    filters: [
      { value: 'all' as BatchLocationFilter, label: 'Missing AI content', description: 'Listings without AI title or description' },
    ],
    defaultFilter: 'all' as BatchLocationFilter,
  },
  {
    value: 'batch_classify_type' as const,
    label: 'Classify type, styles & features',
    description: 'AI picks the category, subtypes, styles and features. Anything set by hand is kept.',
    icon: Shapes,
    filters: [
      { value: 'all' as BatchLocationFilter, label: 'Needs classification', description: 'Category not yet chosen by AI or by hand, or styles and features never classified' },
    ],
    defaultFilter: 'all' as BatchLocationFilter,
  },
  {
    value: 'batch_resync_content' as const,
    label: 'Re-sync AI Content',
    description: 'Re-push AI title & description, and AI category, styles & features, to already-published listings.',
    icon: RefreshCw,
    filters: [
      { value: 'all' as BatchLocationFilter, label: 'Published AI listings', description: 'Already-published listings with an AI title, description, category, styles or features' },
    ],
    defaultFilter: 'all' as BatchLocationFilter,
  },
];

type OperationTypeValue = (typeof OPERATION_TYPES)[number]['value'];

export function CreateBatchDialog({
  open,
  onOpenChange,
  defaultPartnerAccountId,
  onSuccess,
}: CreateBatchDialogProps): JSX.Element {
  const { toast } = useToast();

  const [operationType, setOperationType] = useState<OperationTypeValue>('auto_enrich');
  const [partnerAccountId, setPartnerAccountId] = useState<string>(defaultPartnerAccountId ?? '');
  const [locationFilter, setLocationFilter] = useState<BatchLocationFilter>('non_enriched');
  const [preview, setPreview] = useState<BatchOperationPreview | null>(null);
  const [bulkUpdateAttributes, setBulkUpdateAttributes] = useState<Record<string, unknown>>({});
  const [forcePublish, setForcePublish] = useState(false);
  const [reclassifyAll, setReclassifyAll] = useState(false);

  const { data: partnerAccountsData, isLoading: loadingAccounts } = usePartnerAccounts();
  const getPreview = useGetBatchPreview();
  const createOperation = useCreateBatchOperation();

  // Update location filter and reset force when operation type changes
  useEffect(() => {
    const opType = OPERATION_TYPES.find((t) => t.value === operationType);
    if (opType) {
      setLocationFilter(opType.defaultFilter);
    }
    setForcePublish(false);
    setReclassifyAll(false);
  }, [operationType]);

  // Fetch preview when selections change
  useEffect(() => {
    if (partnerAccountId) {
      setPreview(null);
      const config =
        operationType === 'batch_classify_type' && reclassifyAll ? { reclassify: true } : undefined;
      getPreview.mutate(
        {
          operationType,
          partnerAccountId,
          locationFilter,
          ...(config ? { config } : {}),
        },
        {
          onSuccess: (data) => {
            setPreview(data);
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getPreview.mutate is stable, only re-run on input changes
  }, [partnerAccountId, operationType, locationFilter, reclassifyAll]);

  const handleCreate = (): void => {
    if (!partnerAccountId) {
      toast({
        title: 'Error',
        description: 'Please select a partner account',
        variant: 'destructive',
      });
      return;
    }

    const config: Record<string, unknown> = {};
    if (operationType === 'bulk_update') {
      config.attributes = bulkUpdateAttributes;
    }
    if (forcePublish && (operationType === 'batch_publish' || operationType === 'auto_enrich')) {
      config.force = true;
    }
    if (reclassifyAll && operationType === 'batch_classify_type') {
      config.reclassify = true;
    }

    createOperation.mutate(
      {
        operationType,
        partnerAccountId,
        locationFilter,
        ...(Object.keys(config).length > 0 ? { config } : {}),
      },
      {
        onSuccess: () => {
          toast({
            title: 'Batch Operation Created',
            description: `Processing ${String(preview?.eligibleCount ?? 0)} locations`,
          });
          onOpenChange(false);
          onSuccess?.();
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error instanceof Error ? error.message : 'Failed to create batch operation',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const selectedOpType = OPERATION_TYPES.find((t) => t.value === operationType);
  const partnerAccounts = partnerAccountsData ?? [];

  // Get display label for operation type
  const getOperationLabel = (value: OperationTypeValue): string => {
    const op = OPERATION_TYPES.find((t) => t.value === value);
    return op?.label ?? value;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]" data-testid="create-batch-dialog">
        <DialogHeader>
          <DialogTitle>Create Batch Operation</DialogTitle>
          <DialogDescription>
            Select an operation type and configure filters for batch processing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Partner Account - First and most important */}
          <div className="space-y-2">
            <Label htmlFor="partner-account">Partner Account</Label>
            <Select
              value={partnerAccountId}
              onValueChange={setPartnerAccountId}
              disabled={loadingAccounts}
            >
              <SelectTrigger id="partner-account" data-testid="batch-partner-account-select">
                <SelectValue placeholder={loadingAccounts ? 'Loading...' : 'Select partner account'} />
              </SelectTrigger>
              <SelectContent>
                {partnerAccounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name} ({account.partnerType?.name ?? 'Unknown'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Operation Type Cards */}
          <div className="space-y-2">
            <Label>Operation Type</Label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {OPERATION_TYPES.map((op) => {
                const Icon = op.icon;
                const isSelected = operationType === op.value;
                return (
                  <button
                    key={op.value}
                    type="button"
                    onClick={() => { setOperationType(op.value); }}
                    data-testid={`batch-operation-${op.value.replace('_', '-')}-btn`}
                    className={cn(
                      'flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-all',
                      'hover:border-primary/50 hover:bg-accent/50',
                      isSelected
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                        : 'border-muted'
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-6 w-6',
                        isSelected ? 'text-primary' : 'text-muted-foreground'
                      )}
                    />
                    <div className="space-y-1">
                      <div
                        className={cn(
                          'font-medium text-sm',
                          isSelected ? 'text-primary' : 'text-foreground'
                        )}
                      >
                        {op.label}
                      </div>
                      <div className="text-xs text-muted-foreground leading-tight">
                        {op.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Location Filter - contextual based on operation type */}
          {selectedOpType && (
            <div className="space-y-2">
              <Label>Filter Locations</Label>
              <div className="flex gap-2">
                {selectedOpType.filters.map((filter) => {
                  const isSelected = locationFilter === filter.value;
                  // Generate testid based on filter value
                  const testIdSuffix = filter.value.replace('_', '-');
                  return (
                    <button
                      key={filter.value}
                      type="button"
                      onClick={() => { setLocationFilter(filter.value); }}
                      data-testid={`batch-filter-${testIdSuffix}-btn`}
                      className={cn(
                        'flex-1 rounded-md border px-4 py-2 text-sm transition-all',
                        'hover:border-primary/50 hover:bg-accent/50',
                        isSelected
                          ? 'border-primary bg-primary/5 font-medium text-primary'
                          : 'border-muted text-muted-foreground'
                      )}
                    >
                      <div className="font-medium">{filter.label}</div>
                      <div className="text-xs opacity-75">{filter.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bulk Update Attributes Form */}
          {operationType === 'bulk_update' && (
            <BulkUpdateAttributesForm onChange={setBulkUpdateAttributes} />
          )}

          {/* Force Publish Checkbox */}
          {(operationType === 'batch_publish') && (
            <div
              className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3"
              data-testid="batch-force-publish-option"
            >
              <Checkbox
                id="force-publish"
                checked={forcePublish}
                onCheckedChange={(checked) => { setForcePublish(checked === true); }}
                data-testid="batch-force-publish-checkbox"
              />
              <div className="space-y-1">
                <label htmlFor="force-publish" className="flex items-center gap-2 text-sm font-medium text-amber-800 cursor-pointer">
                  <AlertTriangle className="h-4 w-4" />
                  Force Publish
                </label>
                <p className="text-xs text-amber-700">
                  Skip soft validation errors (missing bedrooms, bathrooms, city, country, booking hours, cancellation policy, images).
                  Locations with hard errors (no enrichment, no title, no type, blocked, disabled account) will still be skipped.
                </p>
              </div>
            </div>
          )}

          {/* Re-classify Checkbox (Classify Type) */}
          {operationType === 'batch_classify_type' && (
            <div
              className="flex items-start gap-3 rounded-md border p-3"
              data-testid="batch-reclassify-option"
            >
              <Checkbox
                id="reclassify-all"
                checked={reclassifyAll}
                onCheckedChange={(checked) => { setReclassifyAll(checked === true); }}
                data-testid="batch-reclassify-checkbox"
              />
              <div className="space-y-1">
                <label htmlFor="reclassify-all" className="text-sm font-medium cursor-pointer">
                  Re-classify every location
                </label>
                <p className="text-xs text-muted-foreground" data-testid="batch-reclassify-description">
                  By default only locations that still need classification are included. Turn this
                  on to run AI on every location of this account. Anything set by hand is kept: AI
                  never replaces a category, styles or features a person chose.
                </p>
              </div>
            </div>
          )}

          {/* Preview */}
          {partnerAccountId && (
            <div className="rounded-lg border p-4 bg-muted/50" data-testid="batch-preview">
              <div className="text-sm font-medium mb-2">Preview</div>
              {getPreview.isPending ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Counting eligible locations...
                </div>
              ) : preview ? (
                <div className="space-y-1">
                  <p className="text-lg font-semibold" data-testid="batch-preview-count">
                    {String(preview.eligibleCount)} locations
                  </p>
                  <p className="text-xs text-muted-foreground">
                    will be processed by &ldquo;{getOperationLabel(operationType)}&rdquo; operation
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground">
                  Select options to see eligible locations
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { onOpenChange(false); }}
            data-testid="batch-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!partnerAccountId || !preview || preview.eligibleCount === 0 || createOperation.isPending || (operationType === 'bulk_update' && Object.keys(bulkUpdateAttributes).length === 0)}
            data-testid="batch-create-btn"
          >
            {createOperation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              `Start ${getOperationLabel(operationType)} (${String(preview?.eligibleCount ?? 0)} locations)`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
